import React, {useContext, useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {useAtom} from "jotai";
import {Box, IconButton, InputBase, Stack, Typography, useMediaQuery, useTheme} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {CustomerSupportIcon, SendIcon} from "@hugeicons/core-free-icons";
import {useRegistryProject} from "../RegistryProjectContext";
import {useSubProjectFlows} from "../useSubProjectFlows";
import {useStompWebsocket} from "../../../hooks/useStompWebsocket";
import {SignInContext} from "../../../context/SignInContext";
import {ConversationRepository} from "../../../repositories/ConversationRepository";
import {PANEL_SX} from "../dashboard/components/Panel";
import {ConversationModal} from "../dashboard/components/ConversationModal";
import {SelectedConversationAtom} from "../../project/dashboard/atoms";
import {SupervisionList} from "./SupervisionList";
import {SupervisionDetail} from "./SupervisionDetail";

// Project type -> conversation type
const CONVERSATION_TYPES = {VOICE_ASSISTANT: "VOICE_BOT", CHATBOT: "CHAT_BOT"};

// Chatbot projects can start a test conversation on the selected language's flow.
const TestChatForm = ({flow, onStarted, sx}) => {
  const {currentUser} = useContext(SignInContext);
  const {members} = useRegistryProject();
  const [message, setMessage] = useState("");

  const start = (e) => {
    e.preventDefault();
    if (!message.trim() || !flow?.id) return;
    // the user's VDN from the project members (the flow's vdnManager is no longer updated)
    const vdn = members.find(member => member.username === currentUser?.username)?.vdn;
    const sender = localStorage.getItem("sender") || vdn || -999;
    ConversationRepository.createConversation({flowUid: flow.id, sender, message, type: "CHAT_BOT"})
      .then(response => {
        setMessage("");
        if (response?.data?.id) onStarted(response.data);
      })
      .catch(error => console.log(error));
  };

  return (
    <Box component={"form"} onSubmit={start} sx={{
      display: "flex", alignItems: "center", gap: 1, pl: 1.75, pr: 0.5, height: 44, borderRadius: "12px",
      border: "1px solid #E4E4EE", bgcolor: "#fff", "&:focus-within": {borderColor: "#8491F7"}, ...sx,
    }}>
      <InputBase
        placeholder={"Start a test conversation…"}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        sx={{flex: 1, fontSize: 14}}
      />
      <IconButton type={"submit"} disabled={!message.trim()} sx={{color: "#4B55C9"}}>
        <HugeiconsIcon icon={SendIcon} size={18}/>
      </IconButton>
    </Box>
  );
};

// Nothing selected
const EmptyState = ({isChat, flow, onStarted}) => {
  return (
    <Stack alignItems={"center"} justifyContent={"center"} gap={1.5} sx={{height: "100%", px: 3, textAlign: "center"}}>
      <Box sx={{
        width: 56, height: 56, borderRadius: "16px", display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: "rgba(132, 145, 247, 0.12)", color: "#8491F7",
      }}>
        <HugeiconsIcon icon={CustomerSupportIcon} size={26}/>
      </Box>
      <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D"}}>Select a conversation</Typography>
      <Typography sx={{fontSize: 13, color: "#8A8AA0", maxWidth: 360}}>
        Pick a conversation from the list to follow it live, supervise it or hand it over.
      </Typography>
      {isChat && flow?.id && <TestChatForm flow={flow} onStarted={onStarted} sx={{mt: 2, width: "100%", maxWidth: 420}}/>}
    </Stack>
  );
};

// Live supervision of the project's conversations (every language, or one picked in the list).
// The open conversation is kept in the URL (?conversation=123).
export const RegistrySupervisionPage = () => {
  const {project, subProject} = useRegistryProject();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = searchParams.get("conversation");

  // "" = every language
  const [subProjectId, setSubProjectId] = useState("");
  const lookups = useSubProjectFlows(project);
  const conversationType = CONVERSATION_TYPES[project.type] || "VOICE_BOT";

  // Conversations created or changed, and the live number of active ones, for the picked language or the project
  const scope = subProjectId ? `/topic/sub-project/${subProjectId}` : `/topic/project/${project.id}`;
  const [conversationUpdate] = useStompWebsocket(`${scope}/conversations`);
  const [activeUpdate] = useStompWebsocket(`${scope}/active-conversations`);
  const activeCount = activeUpdate && (!activeUpdate.conversationType || activeUpdate.conversationType === conversationType)
    ? Number(activeUpdate.active) || 0
    : undefined;

  const [selectedConversation, setSelectedConversation] = useAtom(SelectedConversationAtom);
  useEffect(() => () => setSelectedConversation(null), [setSelectedConversation]);

  useEffect(() => {
    if (!isMobile || !conversationUpdate?.id) return;
    setSelectedConversation(current => current?.id === conversationUpdate.id
      ? {
        ...current,
        ...conversationUpdate,
        sender: current.sender,
        senderName: current.senderName ?? conversationUpdate.senderName,
      }
      : current);
  }, [isMobile, conversationUpdate, setSelectedConversation]);

  const [orderedConversations, setOrderedConversations] = useState([]);
  const selectedIndex = orderedConversations.findIndex(item => String(item.id) === String(selectedConversation?.id));
  const navigation = {
    prev: selectedIndex > 0 ? orderedConversations[selectedIndex - 1] : null,
    next: selectedIndex >= 0 ? orderedConversations[selectedIndex + 1] || null : null,
  };

  const select = (conversation) => setSearchParams(conversation ? {conversation: String(conversation.id)} : {});

  const list = (
    <SupervisionList
      projectId={project.id}
      subProjects={project.subProjects}
      conversationType={conversationType}
      subProjectId={subProjectId}
      onChangeLanguage={setSubProjectId}
      lookups={lookups}
      selectedId={isMobile ? selectedConversation?.id : conversationId}
      onSelect={isMobile ? setSelectedConversation : select}
      conversationUpdate={conversationUpdate}
      activeUpdate={activeUpdate}
      activeCount={activeCount}
      onOrderChange={setOrderedConversations}
    />
  );

  const detail = conversationId && (
    <SupervisionDetail
      key={conversationId}
      conversationId={conversationId}
      flowOf={lookups.flowOf}
      fallbackFlow={lookups.flows[subProject.flowUid] || null}
      subProjectOf={lookups.subProjectOf}
      tagsOf={lookups.tagsOf}
      conversationUpdate={conversationUpdate}
      onOpen={select}
      onClose={() => select(null)}
    />
  );

  if (isMobile) {
    return (
      <Box sx={{flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1.5, p: 1.5}}>
        {conversationType === "CHAT_BOT" && lookups.flows[subProject.flowUid]?.id && (
          <Box sx={{...PANEL_SX, flexShrink: 0, p: 1}}>
            <TestChatForm
              flow={lookups.flows[subProject.flowUid]}
              onStarted={setSelectedConversation}
              sx={{border: "none", bgcolor: "#FAFAFD", "&:focus-within": {bgcolor: "rgba(132, 145, 247, 0.08)"}}}
            />
          </Box>
        )}
        <Box sx={{...PANEL_SX, flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column"}}>
          {list}
        </Box>
        <ConversationModal
          flow={selectedConversation ? lookups.flowOf(selectedConversation) : null}
          subProjectOf={lookups.subProjectOf}
          tagsOf={lookups.tagsOf}
          showSupervise
          navigation={navigation}
        />
      </Box>
    );
  }

  return (
    <Box sx={{flex: 1, minHeight: 0, display: "flex", gap: 2, p: 2}}>
      <Box sx={{...PANEL_SX, width: {md: 340, xl: 380}, flexShrink: 0, overflow: "hidden", display: "flex", flexDirection: "column"}}>
        {list}
      </Box>
      <Box sx={{...PANEL_SX, flex: 1, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column"}}>
        {detail || (
          <EmptyState
            isChat={conversationType === "CHAT_BOT"}
            flow={lookups.flows[subProject.flowUid]}
            onStarted={select}
          />
        )}
      </Box>
    </Box>
  );
};
